import { Head, Link } from "@inertiajs/react"
import { ChevronRight, Link2, Tag } from "lucide-react"
import { AppShell } from "@/components/AppShell"
import { Badge } from "@/components/ui/badge"
import {
  Breadcrumb,
  categoryLabel,
  type Crumb,
  Pagination,
  ReviewButton,
  SearchForm,
  type PageMeta,
} from "./shared"

type LeafRow = {
  id: number
  canonical_name: string
  pack_size: string | null
  variant_name: string | null
  range_name: string | null
  star_product_code: string | null
  niq_key: string | null
  is_own: boolean
  status: string | null
  alias_count: number
  link_count: number
}

type Props = {
  manufacturer: { id: number; name: string }
  brand: { id: number; name: string; is_own: boolean; tracked_category: string | null }
  leaves: LeafRow[]
  pagination: PageMeta
  filters: { search: string | null }
  breadcrumb: Crumb[]
}

export default function Leaves({ manufacturer, brand, leaves, pagination, filters, breadcrumb }: Props) {
  const path = "/admin/product-master"
  const query = { brand_id: String(brand.id), search: filters.search }
  const description = `Produk (leaf ukuran SKU) di bawah merek ${brand.name} — ${manufacturer.name}.`

  return (
    <>
      <Head title={`${brand.name} — Produk Master`}>
        <meta name="description" content={description} />
        <meta property="og:title" content={`${brand.name} — Produk Master`} />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell width="wide">
        <Breadcrumb crumbs={breadcrumb} />
        <div className="border-b border-hairline pb-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex flex-wrap items-center gap-3">
              <h1>{brand.name}</h1>
              {categoryLabel(brand.tracked_category) && (
                <Badge tone="neutral">{categoryLabel(brand.tracked_category)}</Badge>
              )}
            </div>
            <ReviewButton />
          </div>
        </div>

        <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-end">
          <SearchForm
            path={path}
            query={query}
            initial={filters.search ?? ""}
            placeholder="Nama produk, kode STAR, atau NIQ key"
          />
        </div>

        {leaves.length === 0 ? (
          <div className="mt-10 rounded-lg border border-hairline bg-surface p-10 text-center">
            <p className="text-ink-muted">Tidak ada produk yang cocok.</p>
          </div>
        ) : (
          <>
            <div className="mt-6 hidden overflow-x-auto rounded-lg border border-hairline md:block">
              <table className="w-full text-sm">
                <thead className="bg-surface text-left text-ink-muted">
                  <tr>
                    <th className="px-4 py-3 font-medium">Produk (leaf)</th>
                    <th className="px-4 py-3 font-medium">Ukuran</th>
                    <th className="px-4 py-3 font-medium">Identitas</th>
                    <th className="px-4 py-3 font-medium">Crosswalk</th>
                    <th className="w-10 px-4 py-3" aria-label="Buka" />
                  </tr>
                </thead>
                <tbody>
                  {leaves.map((v) => (
                    <tr key={v.id} className="border-t border-hairline hover:bg-surface">
                      <td className="px-4 py-3">
                        <Link href={`${path}/${v.id}`} className="font-medium">
                          {v.canonical_name}
                        </Link>
                        {v.variant_name && <div className="text-xs text-ink-muted">{v.variant_name}</div>}
                      </td>
                      <td className="px-4 py-3 text-ink-body">{v.pack_size ?? "—"}</td>
                      <td className="px-4 py-3 text-ink-muted">
                        {v.star_product_code ? (
                          <span className="font-mono text-xs">{v.star_product_code}</span>
                        ) : v.niq_key ? (
                          <span className="font-mono text-xs">{v.niq_key}</span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1 text-xs text-ink-muted">
                          <span className="inline-flex items-center gap-1">
                            <Tag className="h-3 w-3" aria-hidden="true" /> {v.alias_count}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <Link2 className="h-3 w-3" aria-hidden="true" /> {v.link_count}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-ink-muted">
                        <Link href={`${path}/${v.id}`} aria-label={`Buka ${v.canonical_name}`}>
                          <ChevronRight className="h-4 w-4" aria-hidden="true" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-6 flex flex-col gap-3 md:hidden">
              {leaves.map((v) => (
                <Link
                  key={v.id}
                  href={`${path}/${v.id}`}
                  className="rounded-lg border border-hairline bg-surface p-4"
                >
                  <div className="font-medium text-ink-body">{v.canonical_name}</div>
                  <div className="mt-1 text-xs text-ink-muted">
                    {v.pack_size ?? "—"} · {v.star_product_code ?? v.niq_key ?? "—"}
                  </div>
                  <div className="mt-1 text-xs text-ink-muted">
                    {v.alias_count} alias · {v.link_count} tautan NIQ
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}

        <Pagination meta={pagination} path={path} query={query} />
      </AppShell>
    </>
  )
}
